import { ResourceTier as RT } from '../types/entities.js';
import { hash, SeededRandom } from './hash.js';
import { MASTER_SEED, SECTOR_SIZE, RACE_INFO, RACE_RESOURCES, RESOURCE_INFO, ALL_RESOURCE_TIERS, } from '../constants/game.js';
/**
 * Planetary-surface procedural generation.
 *
 * Surfaces are a second flavour of `sector` that the world manager
 * activates when a player lands on a planet from orbit. They share the
 * same wire format (`Entity`), the same per-sector simulation budget,
 * and the same persistence pattern as space sectors — but their
 * blueprint type is different because what lives in a surface is
 * different from what lives in a space sector:
 *
 *   - A **heightmap** (Uint8Array, 128×128 by default) defining the
 *     ground at any (x, z) within the playable bounds. The map is
 *     **not** a 3D voxel field — Battlezone-style 2.5D terrain. Above
 *     the heightmap, gameplay is fully 3D: ships fly, climb, dive,
 *     and dogfight exactly as they do in space, with a single
 *     downward gravity force pulling them back toward the ground.
 *   - A small handful of {@link MilitaryBase}s placed deterministically
 *     by the generator so both clients and the server agree on where
 *     the bases live without persisting per-base state.
 *   - {@link ResourcePatch} clusters that the world manager hydrates
 *     into normal `EntityType.Asteroid` mineable nodes — replacing
 *     orbital asteroid fields with ground-level deposits.
 *   - {@link SurfacePatrolPath} polylines (A*-derived at gen time)
 *     that NPC patrols follow between bases. Storing the polyline
 *     means server/clients never have to re-run A* at runtime.
 *   - {@link SurfaceNpcBlueprint}s for the per-base patrol roster.
 *
 * Generation is **pure + deterministic** — given the same
 * `(parentSectorCoord, planetIndex)` it always produces the same
 * blueprint, on every server and every client, byte-for-byte. This is
 * what lets the persistence layer use the SAME generate-on-first-
 * discovery, store-and-rehydrate pattern as space sectors: the first
 * lander triggers a `save()`, every subsequent lander reads back the
 * exact same map. A surface is identified by the string id
 * `surface:<sx>,<sy>,<sz>:<planetIndex>` (see {@link surfaceId}).
 */
/**
 * Side length of the playable surface in world units (square footprint
 * with a *circular* rim ramp baked into the heightmap — see
 * {@link fbmHeightmap}).
 *
 * Bumped from 1600 → 2800 in v6 so a planet feels like an actual
 * explorable map instead of a small dogfight arena: at BASE_MAX_SPEED
 * a corner-to-corner traversal now takes ~13 s straight-line and a
 * meaningful exploration loop (visit each base + a few resource
 * patches + the rim) takes well over a minute. We keep the heightmap
 * resolution constant (128² cells) — each cell now covers ~22 m
 * of world instead of ~13 m, which makes the silhouette of distant
 * features (mountains, plateaus) read slightly smoother but doesn't
 * change the GPU cost of the terrain mesh (still one ~16 K-vert draw
 * call) or the InstancedMesh cost of the flora pass. Resource patch,
 * NPC, and flora counts scale linearly with `SURFACE_SIZE / 1600`
 * inside the generators so the new arena doesn't feel empty.
 */
export const SURFACE_SIZE = 2800;
/**
 * Bumped any time the surface generator changes shape in a way that
 * invalidates already-persisted blueprints (new fields, larger map,
 * different rim shape, …). The persistence layer compares this
 * against the loaded `blueprint.blueprintVersion` and regenerates on
 * mismatch so an old planet doesn't load with the new client +
 * missing fields.
 *
 * - **v1**: original square arena, square rim, no flora.
 * - **v2**: 1.6× larger arena, *circular* rim mountains, cliff
 *           terracing, base plateaus, carved paths, flora pass.
 * - **v3**: steeper rim wall (Battlezone-style), taller flora,
 *           perimeter mountain peak silhouette data.
 * - **v4**: hard circular boundary inside the rim, collidable
 *           flora (Tree / Mushroom / IcePillar / Crystal /
 *           Boulder), wider tree spacing so colliders don't
 *           form impassable thickets.
 * - **v5**: tighter (trunk-matching) flora colliders + sliding
 *           contact response (was hard rebound) so the player
 *           can graze a forest without the ship freezing every
 *           time it brushes a trunk. Spacing also widened so
 *           even adjacent collidable flora leave a clear lane.
 * - **v6**: 1.75× larger playable area (1600 → 2800 u), three new
 *           alien flora kinds (AlienBulb, TwistedSpire,
 *           JellyFlora), occasional intra-biome lava patches that
 *           damage hovering ships, optional pirate hostile patrols
 *           on otherwise-friendly worlds, snappier higher-arc
 *           jump impulse, taller jet ceiling so the new jump apex
 *           isn't clipped, and surface mineral nodes that broadcast
 *           a distinct name + visual flag (`_extra.surfaceMineral`)
 *           so the client paints them as crystalline outcrops
 *           instead of recycling the orbital asteroid mesh.
 */
export const SURFACE_BLUEPRINT_VERSION = 7;
/**
 * Biome fauna kinds (Phase 3.1). Picked per-biome by the surface
 * generator and spawned as `EntityType.NPC` entities with
 * `_extra.fauna === true` and `_extra.faunaKind === FaunaKind`. The
 * server's fauna AI branch in `world-manager.ts` reads the kind to
 * choose attack pattern, ranged/melee preference, and movement style.
 *
 * Kept as a numeric enum so it serialises compactly in the entity
 * `_extra` blob. Values are stable wire — never renumber.
 */
export var FaunaKind;
(function (FaunaKind) {
    FaunaKind[FaunaKind["RockGrub"] = 0] = "RockGrub";
    FaunaKind[FaunaKind["LavaWyrm"] = 1] = "LavaWyrm";
    FaunaKind[FaunaKind["IceSerpent"] = 2] = "IceSerpent";
    FaunaKind[FaunaKind["BiofluxStrider"] = 3] = "BiofluxStrider";
    FaunaKind[FaunaKind["DustReaver"] = 4] = "DustReaver";
})(FaunaKind || (FaunaKind = {}));
/**
 * Heightmap resolution. `SURFACE_SIZE / SURFACE_HEIGHTMAP_RES` units
 * per cell ≈ 8 m at the default 128×128. Bumping to 256 quadruples
 * memory + Mongo doc size for marginal terrain detail; 64 is too
 * coarse to read as terrain. 128 is the sweet spot.
 */
export const SURFACE_HEIGHTMAP_RES = 128;
/**
 * Maximum altitude (above the lowest point on the map) the heightmap
 * can encode. Stored as a `Uint8` 0..255 ratio; the renderer + collider
 * multiply back through this constant to recover world units.
 *
 * 80 units gives roughly 25 % of `SURFACE_SIZE / 4` so a typical
 * mountain ridge crests a few ship-lengths above the valley floor —
 * enough to read as "ground" without producing vertical walls that a
 * Scout couldn't fly over.
 */
export const SURFACE_MAX_HEIGHT = 120;
/**
 * Atmosphere ceiling in world units (relative to the lowest point of
 * the map). Crossing it triggers automatic liftoff back to orbit; the
 * client also fades in the orbital starfield as the player approaches
 * it. Sized so a player can climb high enough to engage targets in 3D
 * but cannot escape the surface arena into the orbital sector by
 * accident — that's what the Land Pad / Liftoff control is for.
 */
export const SURFACE_CEILING = SURFACE_MAX_HEIGHT + 360;
/**
 * Gravity acceleration in world units / s². Combined with the global
 * `MAX_SPEED` and ship hover offset, this produces a comfortable
 * ~1.5 s "stop and fall" feel from the ceiling, which is fast enough
 * to feel grounded but slow enough that the UP control still lifts
 * the ship reliably.
 */
export const SURFACE_GRAVITY = 18;
/**
 * Authoritative physics constants used by BOTH the server
 * (`entity-manager.ts → MAX_SPEED / THRUST_FORCE`) and the client
 * predictor (`client/state.ts → applyLocalInput`). They live in
 * `shared` so the predicted velocity stays phase-locked with the
 * server's authoritative tick — otherwise the predictor drifts
 * every frame the pilot is pitched at the terrain (i.e. the entire
 * time on a planet) and the resulting reconcile bleed manifests
 * as the user-reported "ship freezes for a beat then snaps to its
 * expected destination" stutter.
 *
 *   BASE_MAX_SPEED       absolute velocity ceiling for the
 *                        `basic_thruster` engine. Engine.maxSpeed
 *                        is treated as a ratio vs ENGINE_BASELINE
 *                        and multiplied into this cap.
 *   BASE_THRUST_FORCE    acceleration scalar fed into the input
 *                        branch. Multiplied by engine.acceleration
 *                        ratio, boostMultiplier, race trait
 *                        thrustMul, and ship-class thrustMul.
 *
 * Keep these literals here ONLY. Re-importing into the server keeps
 * a single source of truth, so an accidental edit can't silently
 * desync the two physics paths.
 */
export const BASE_MAX_SPEED = 300;
export const BASE_THRUST_FORCE = 200;
/**
 * Default hover offset above the heightmap when a ship is at rest on
 * the ground. Chosen so even the largest hulls (Mothership visual
 * scale 48) clear the terrain comfortably; smaller ships look fine
 * with the same value because the camera is rigidly chained to the
 * ship pose.
 *
 * Per-class hover tuning is intentionally NOT done here — repo rule 5
 * (PvP parity) means hover height must be class-cosmetic only and
 * never granted as a strict combat advantage.
 */
export const SURFACE_HOVER_OFFSET = 4;
/**
 * Soft altitude lid (world units above the terrain directly under the
 * ship) the gravity branch enforces by aggressively damping any
 * upward velocity AND zeroing any vy that's still positive once we're
 * above the cap. Going *up* still works — the player can hop or jet
 * a small distance to clear obstacles — but they can't climb to orbit
 * by holding any combination of pitch + thrust. Tuned so the
 * {@link SURFACE_JUMP_VELOCITY} jump apex (≈81 u above the resting
 * hover at our gravity) lands neatly under the cap with a small
 * margin for ridges — anything higher and the cap would clip the
 * top of the natural jump arc and the user would feel a "wall"
 * mid-air; anything lower and a sustained pitch+W could outrun the
 * cap on heavy ships.
 */
export const SURFACE_JET_CEILING = 95;
/**
 * Per-second drag applied to upward velocity while above
 * {@link SURFACE_JET_CEILING}. 0.7 means ~30 % of remaining vy is
 * removed per second above the cap. Combined with the hard
 * `vy = max(vy, 0)` clamp the gravity branch applies once the ship
 * is past the cap, this just smoothes the brief instant of overshoot
 * before the clamp grabs hold so the camera doesn't jolt.
 */
export const SURFACE_JET_OVERSHOOT_DRAG = 0.7;
/**
 * Fixed upward velocity an UP-key jump grants when the ship is on
 * (or near) the ground. Bumped to 54 in v6.1 so the jump reads as
 * a real ground-vehicle thruster hop instead of a polite curtsy:
 * at {@link SURFACE_GRAVITY} = 18 u/s² the apex now sits ≈ 81 u
 * above the resting hover (still under {@link SURFACE_JET_CEILING})
 * and the airborne arc takes ≈ 6 s round-trip. That clears any
 * Spaceport tower (~17 u), Fortress wall (~22 u), even most rim
 * foothills, and the natural fall back to the ground reads as a
 * heavy hop instead of an awkward float.
 *
 * Critical: this is the *only* path through which an UP press can
 * change the ship's vertical velocity on a planet — holding the key
 * does not produce continuous lift (see entity-manager.applyInput).
 *
 * PvP parity (repo rule 5): same constant for every race / ship
 * class — the hop is a navigation aid, never a strict combat
 * advantage. Heavier hulls fall a hair faster only because of the
 * uniform gravity, not because of any per-class divisor.
 */
export const SURFACE_JUMP_VELOCITY = 54;
/**
 * Cooldown between successive jumps in milliseconds. Tightened from
 * 1500 → 1100 in v6 to match the snappier jump apex — the natural
 * airborne time of the new jump (≈ 4.2 s round-trip) is still
 * comfortably longer than the cooldown, but the input feedback
 * loop feels noticeably tighter when a held UP can re-fire as soon
 * as the wheels touch the ground.
 */
export const SURFACE_JUMP_COOLDOWN_MS = 1100;
/**
 * Distance above ground at which the ship is considered "grounded"
 * for the purposes of allowing a jump. Generous enough that small
 * terrain wobble between server snapshots doesn't reject a jump
 * input, tight enough that you can't jump in mid-air.
 */
export const SURFACE_JUMP_GROUNDED_TOLERANCE = SURFACE_HOVER_OFFSET * 1.6;
/**
 * Maximum amount of vertical velocity preserved on a hard landing.
 * Anything faster than this gets clamped to zero so a ship that
 * pancakes on the ground doesn't immediately bounce back into the
 * sky. Tuned to the same order as gravity per second so a freefall
 * from the ceiling lands hard but recoverable.
 */
export const SURFACE_LANDING_VY_MIN = -2;
/**
 * Per-second hull damage dealt while a ship hovers low over a lava
 * surface (Lava biome ground or an inline lava pool). Applied only
 * when the ship is within {@link SURFACE_LAVA_DANGER_ALTITUDE} of
 * the heightmap — at higher altitudes the damage drops to zero so
 * the player can fly through a lava biome at a sensible cruise
 * altitude without burning. 10 hp/s is a strong nudge to climb
 * (a 100-hp Scout has 10 s to escape, a 400-hp Cruiser has 40 s)
 * without insta-killing anyone who briefly grazes the surface.
 *
 * Critically PvP-neutral (repo rule 5): same constant for every
 * race and ship class — bigger hulls just have more headroom by
 * virtue of their base HP pool, which is the same trade-off they
 * already make against every other damage source.
 */
export const SURFACE_LAVA_DPS = 10;
/**
 * Altitude above the ground at which the lava-damage tick switches
 * off. Anything above this and the ship is considered "cruising
 * over" the lava; below it the heat counts. Matched to the
 * resting hover offset + a small buffer so a parked ship inside
 * a Spaceport on a Lava biome doesn't take damage for sitting
 * still.
 */
export const SURFACE_LAVA_DANGER_ALTITUDE = SURFACE_HOVER_OFFSET * 4;
/**
 * Contact response factor for a ship grazing a flora collider
 * (tree trunk, mushroom stem, …). At `1.0` the ship just loses
 * the inward component of its velocity — it doesn't rebound — so
 * the contact reads as a "slide along the obstacle" rather than
 * a pinball bounce. Anything `> 1.0` would reflect velocity
 * outward (ping-pong off the trunk and visibly stutter), and
 * `< 1.0` would let the ship push *into* the trunk by a small
 * amount each frame (sticky / clipping).
 *
 * Why `1.0` instead of a small bounce: the previous build used
 * `1.4` which reversed forward velocity into a ~30 % backward
 * impulse on contact. Combined with the predictor and the
 * authoritative server bouncing in slightly different orders
 * across the network jitter window, the resulting reconcile gap
 * was visible as the user-reported "ship freezes for a beat
 * then snaps to where it should have been" while flying through
 * a forested area.
 */
export const SURFACE_FLORA_BOUNCE = 1.0;
/**
 * Same idea as {@link SURFACE_FLORA_BOUNCE} but for the rim
 * boundary. The mountain ring is meant to read as a hard wall
 * the player slides along, not a trampoline that flings them
 * back into the arena, so we use a pure slide here too.
 */
export const SURFACE_WALL_BOUNCE = 1.0;
/**
 * Distance from a planet (in world units) at which a player may
 * request to land. Mirrors `WARP_GATE_DISTANCE` — small enough that
 * the player has to actually approach the planet, large enough that
 * they don't crash into the atmosphere first.
 */
export const SURFACE_LAND_DISTANCE = 80;
/**
 * Six biome buckets — kept small on purpose so per-biome material art
 * stays manageable. The numeric value is used in serialized blueprints
 * (Mongo + wire), so do NOT renumber existing entries.
 */
export var BiomeId;
(function (BiomeId) {
    BiomeId[BiomeId["Dirt"] = 0] = "Dirt";
    BiomeId[BiomeId["Grass"] = 1] = "Grass";
    BiomeId[BiomeId["Snow"] = 2] = "Snow";
    BiomeId[BiomeId["Lava"] = 3] = "Lava";
    BiomeId[BiomeId["Rock"] = 4] = "Rock";
    BiomeId[BiomeId["Ice"] = 5] = "Ice";
})(BiomeId || (BiomeId = {}));
export const BIOME_PALETTE = {
    [BiomeId.Dirt]: { ground: 0x8a5a3c, cliff: 0x6b4530, sky: 0xd58b58, sun: 0xfff0c0, fog: 0x6e3a22, ambient: 0x553322 },
    [BiomeId.Grass]: { ground: 0x4a8a3c, cliff: 0x6b6535, sky: 0x9bbeff, sun: 0xfff5d0, fog: 0x445a55, ambient: 0x556644 },
    [BiomeId.Snow]: { ground: 0xe5eef5, cliff: 0x8a98a8, sky: 0xb5cae0, sun: 0xffffff, fog: 0x8aa2b8, ambient: 0x445566 },
    [BiomeId.Lava]: { ground: 0x4a1a14, cliff: 0x2a0a08, sky: 0xff4422, sun: 0xffaa44, fog: 0x882211, ambient: 0x442211 },
    [BiomeId.Rock]: { ground: 0x707078, cliff: 0x404048, sky: 0x445566, sun: 0xddddee, fog: 0x223344, ambient: 0x334455 },
    [BiomeId.Ice]: { ground: 0xc6e0f0, cliff: 0x6b8aa8, sky: 0x6688aa, sun: 0xeeffff, fog: 0x445566, ambient: 0x334455 },
};
/**
 * Resource-tier preferences per biome. The generator uses these to bias
 * which mineable patches appear in which biomes — e.g. lava biomes
 * favour PlasmaCore + SolarPlasma, ice biomes favour CrystalShards.
 *
 * The list is a *weighted suggestion*; the generator still cross-checks
 * the parent sector's distance against `RESOURCE_INFO[t].minSectorDistance`
 * before placing a tier, so deep-tier resources never spawn in inner
 * sectors no matter what biome rolls.
 */
const BIOME_RESOURCE_BIAS = {
    [BiomeId.Dirt]: [RT.IronOre, RT.CopperOre, RT.ScrapMetal, RT.Silicate],
    [BiomeId.Grass]: [RT.Bioflux, RT.IronOre, RT.ScrapMetal, RT.CrystalShards],
    [BiomeId.Snow]: [RT.CrystalShards, RT.PhasePrism, RT.ChronoQuartz, RT.Silicate],
    [BiomeId.Lava]: [RT.PlasmaCore, RT.SolarPlasma, RT.NeutroniumDust, RT.StarforgeIngot],
    [BiomeId.Rock]: [RT.IronOre, RT.TitaniumAlloy, RT.Silicate, RT.NeutroniumDust],
    [BiomeId.Ice]: [RT.CrystalShards, RT.PhasePrism, RT.VoidEssence, RT.EchoCrystal],
};
/**
 * Discoverable surface ruin / dig site. Comes in three flavours
 * sharing the same data shape so the client renderer can branch on
 * `kind` for the visual; the server treats them uniformly for the
 * Excavation / Investigate path.
 */
export var RuinKind;
(function (RuinKind) {
    /** Half-buried derelict hull pieces with scorch decals. */
    RuinKind[RuinKind["Crash"] = 0] = "Crash";
    /** Alien obelisks + glowing runes. */
    RuinKind[RuinKind["Precursor"] = 1] = "Precursor";
    /** Collapsed prefab walls + storage crates. */
    RuinKind[RuinKind["Colony"] = 2] = "Colony";
})(RuinKind || (RuinKind = {}));
export var BaseKind;
(function (BaseKind) {
    BaseKind[BaseKind["Outpost"] = 0] = "Outpost";
    BaseKind[BaseKind["Fortress"] = 1] = "Fortress";
    BaseKind[BaseKind["Refinery"] = 2] = "Refinery";
    BaseKind[BaseKind["Spaceport"] = 3] = "Spaceport";
})(BaseKind || (BaseKind = {}));
export var FloraKind;
(function (FloraKind) {
    /** Coniferous tree silhouette — Grass, Dirt biomes. */
    FloraKind[FloraKind["Tree"] = 0] = "Tree";
    /** Twisted alien shrub — Grass, Dirt biomes. */
    FloraKind[FloraKind["Shrub"] = 1] = "Shrub";
    /** Glowing crystal cluster — Ice, Snow, Rock biomes. */
    FloraKind[FloraKind["Crystal"] = 2] = "Crystal";
    /** Boulder / rock pile — Rock, Dirt biomes. */
    FloraKind[FloraKind["Boulder"] = 3] = "Boulder";
    /** Lava shard / obsidian spike — Lava biome. */
    FloraKind[FloraKind["LavaShard"] = 4] = "LavaShard";
    /** Pillar of layered ice — Ice biome. */
    FloraKind[FloraKind["IcePillar"] = 5] = "IcePillar";
    /** Mushroom-like spore cluster — Grass biome. */
    FloraKind[FloraKind["Mushroom"] = 6] = "Mushroom";
    /**
     * Glowing alien bulb on a thick stalk — exotic biosystem prop
     * dropped into Grass / Dirt worlds to break up the otherwise
     * earth-resembling silhouette. Tall enough (≈ 12 u) to be a
     * navigable obstacle but not a horizon-dominating landmark.
     * Slightly emissive so it pops against night-side scenes.
     */
    FloraKind[FloraKind["AlienBulb"] = 7] = "AlienBulb";
    /**
     * Twisted helical spire — basalt/glass spike that corkscrews
     * upward. Pure silhouette piece, no canopy. Common on Rock
     * and Lava biomes, occasional accent in Dirt.
     */
    FloraKind[FloraKind["TwistedSpire"] = 8] = "TwistedSpire";
    /**
     * Floating jelly-like dome anchored by a wispy tether — gives
     * Ice and Snow biomes an alien-ocean vibe (jellyfish frozen
     * in mid-drift) and pairs well with crystal clusters. Cap is
     * translucent + emissive so it reads under low light.
     */
    FloraKind[FloraKind["JellyFlora"] = 9] = "JellyFlora";
})(FloraKind || (FloraKind = {}));
/**
 * Per-flora-kind horizontal collision radius, in template units
 * (multiplied by the instance's `scale * scaleBias` at runtime).
 * Smalls (Shrub, LavaShard) are intentionally `0` — they don't
 * collide so the player can drive over filler vegetation without
 * feeling like they're catching on every pixel of the world. The
 * tall, visually unmissable kinds (Tree, Mushroom, IcePillar,
 * Crystal, Boulder) all collide as upright cylinders so the
 * player has actual ground cover to hide behind.
 *
 * Sized to match the **trunk / stem / core** of the visual, NOT
 * the canopy — the user-reported "ship freezes while moving
 * around the planet" stutter was the predictor and the server
 * disagreeing about who had bounced off an oversized canopy
 * cylinder this tick, then constantly re-reconciling the gap.
 * Trunk-sized colliders + sliding contact (see the
 * `SURFACE_FLORA_BOUNCE` constant below) lets the ship graze a
 * forest without snagging on phantom collision past the trunk.
 */
const FLORA_COLLIDE_BASE_RADIUS = {
    [FloraKind.Tree]: 1.5,
    [FloraKind.Shrub]: 0,
    [FloraKind.Crystal]: 1.7,
    [FloraKind.Boulder]: 1.8,
    [FloraKind.LavaShard]: 0,
    [FloraKind.IcePillar]: 1.4,
    [FloraKind.Mushroom]: 1.6,
    // Alien props: bulb has a thick stalk (collide), spire is a solid
    // upright rock (collide), jelly is mostly empty space around a
    // wispy tether (no collide so the player can fly through the dome).
    [FloraKind.AlienBulb]: 1.4,
    [FloraKind.TwistedSpire]: 1.6,
    [FloraKind.JellyFlora]: 0,
};
/**
 * Per-flora-kind scale bias applied to the collision radius — must
 * match the same `scaleBias` the renderer applies to the visual
 * mesh in {@link client/src/procedural/surface-renderer.ts}, so the
 * physics cylinder agrees with what the player sees. Out of caution
 * we keep a single source of truth here so a future renderer
 * tweak forces a corresponding physics update.
 */
const FLORA_VISUAL_SCALE_BIAS = {
    [FloraKind.Tree]: 1.4,
    [FloraKind.Shrub]: 1.2,
    [FloraKind.Crystal]: 1.4,
    [FloraKind.Boulder]: 1.3,
    [FloraKind.LavaShard]: 1.5,
    [FloraKind.IcePillar]: 1.5,
    [FloraKind.Mushroom]: 1.6,
    [FloraKind.AlienBulb]: 1.5,
    [FloraKind.TwistedSpire]: 1.5,
    [FloraKind.JellyFlora]: 1.4,
};
/**
 * Collision radius (world units) for a single flora instance.
 * Returns `0` for non-collidable kinds so callers can fast-path skip.
 */
export function floraCollisionRadius(inst) {
    const base = FLORA_COLLIDE_BASE_RADIUS[inst.kind] ?? 0;
    if (base <= 0)
        return 0;
    const bias = FLORA_VISUAL_SCALE_BIAS[inst.kind] ?? 1;
    return base * inst.scale * bias;
}
/**
 * Iterate every flora instance whose collision radius is greater
 * than zero, lifting the radius computation out of the hot path.
 * Both the server tick and the client predictor walk this list
 * each tick, so we precompute once at blueprint-load time and
 * reuse the result.
 */
export function collectFloraColliders(bp) {
    const out = [];
    const flora = bp.flora ?? [];
    for (const f of flora) {
        const r = floraCollisionRadius(f);
        if (r <= 0)
            continue;
        out.push({ x: f.position.x, z: f.position.z, r, rSq: r * r });
    }
    return out;
}
const SURFACE_NAME_PREFIXES = [
    'Aurelia', 'Vandros', 'Theron', 'Calypso', 'Ipsos', 'Eryndor', 'Mirka',
    'Sokar', 'Talven', 'Volkir', 'Drysa', 'Helkar', 'Mythra', 'Ophir',
    'Kheron', 'Tysk', 'Vesper', 'Nerith', 'Saraka', 'Ulmar',
];
const SURFACE_NAME_SUFFIXES = [
    'Prime', 'IV', 'IX', 'Minor', 'Major', 'Reach', 'Hold', 'Crown',
    'Watch', 'Edge', 'Anchor', 'Spire', 'Vale', 'Crest', 'Drift',
];
/** Stable surface id used everywhere: persistence key, sector id, etc. */
export function surfaceId(parentSector, planetIndex) {
    return `surface:${parentSector.x},${parentSector.y},${parentSector.z}:${planetIndex}`;
}
export function isSurfaceId(id) {
    return id.startsWith('surface:');
}
/**
 * Parse a surface id back into its constituent coordinates. Returns
 * `null` for any id that doesn't match the `surface:<x>,<y>,<z>:<i>`
 * shape so callers can early-out without a try/catch.
 */
export function parseSurfaceId(id) {
    if (!isSurfaceId(id))
        return null;
    const rest = id.slice('surface:'.length);
    const colon = rest.lastIndexOf(':');
    if (colon < 0)
        return null;
    const coordStr = rest.slice(0, colon);
    const idxStr = rest.slice(colon + 1);
    const parts = coordStr.split(',').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n)))
        return null;
    const planetIndex = Number(idxStr);
    if (!Number.isFinite(planetIndex))
        return null;
    return {
        parentSector: { x: parts[0], y: parts[1], z: parts[2] },
        planetIndex,
    };
}
/**
 * Sample the heightmap at world coordinates `(x, z)` (surface-local;
 * `(0, 0)` is the centre of the playable square).
 *
 * Returns the absolute terrain height in world units (already
 * multiplied by `maxHeight`). Coordinates outside the playable
 * bounds clamp to the edge — used by the gravity / collision branch
 * so a ship that drifts past the edge still gets a sensible ground
 * level instead of falling forever.
 */
export function sampleHeightmap(bp, x, z) {
    const { heightmap, heightmapRes, maxHeight, size } = bp;
    const half = size / 2;
    // Map world (x, z) into [0, heightmapRes - 1] grid coords.
    const u = ((x + half) / size) * (heightmapRes - 1);
    const v = ((z + half) / size) * (heightmapRes - 1);
    const cu = Math.max(0, Math.min(heightmapRes - 1, u));
    const cv = Math.max(0, Math.min(heightmapRes - 1, v));
    const x0 = Math.floor(cu);
    const z0 = Math.floor(cv);
    const x1 = Math.min(heightmapRes - 1, x0 + 1);
    const z1 = Math.min(heightmapRes - 1, z0 + 1);
    const tx = cu - x0;
    const tz = cv - z0;
    const idx = (gx, gz) => gz * heightmapRes + gx;
    const h00 = heightmap[idx(x0, z0)] ?? 0;
    const h10 = heightmap[idx(x1, z0)] ?? 0;
    const h01 = heightmap[idx(x0, z1)] ?? 0;
    const h11 = heightmap[idx(x1, z1)] ?? 0;
    const a = h00 * (1 - tx) + h10 * tx;
    const b = h01 * (1 - tx) + h11 * tx;
    const ratio = (a * (1 - tz) + b * tz) / 255;
    return ratio * maxHeight;
}
// ─── Internal helpers ────────────────────────────────────────────────
function pickBiome(rng, raceId, sectorDist) {
    // Race lore-bias: Sylphari worlds skew Grass/Bioflux, Krelg/Nova skew
    // Lava, Chronos/Tharix skew Snow/Ice, etc. Falls back to a uniform
    // pick so an unmapped race still produces a varied world.
    const bias = {
        [6]: [BiomeId.Grass, BiomeId.Grass, BiomeId.Dirt], // Sylphari
        [7]: [BiomeId.Lava, BiomeId.Rock, BiomeId.Dirt], // KrelgHegemony
        [8]: [BiomeId.Rock, BiomeId.Ice, BiomeId.Snow], // TharixCabal
        [9]: [BiomeId.Ice, BiomeId.Snow, BiomeId.Rock], // ChronosSeers
        [10]: [BiomeId.Lava, BiomeId.Lava, BiomeId.Rock], // NovaForgers
        [11]: [BiomeId.Ice, BiomeId.Snow, BiomeId.Rock], // AbyssalChorus
        [4]: [BiomeId.Rock, BiomeId.Dirt, BiomeId.Lava], // ObsidianCollective
    };
    const pool = bias[raceId];
    if (pool && pool.length > 0)
        return pool[rng.nextInt(0, pool.length - 1)];
    // Add a slight depth bias — deeper sectors lean toward harsher biomes.
    if (sectorDist > 60 && rng.next() < 0.4) {
        return rng.pick([BiomeId.Lava, BiomeId.Rock, BiomeId.Ice]);
    }
    return rng.pick([BiomeId.Dirt, BiomeId.Grass, BiomeId.Snow, BiomeId.Rock]);
}
function fbmHeightmap(seed, res, biome) {
    const out = new Array(res * res);
    // Cheap deterministic hash-based gradient noise — value-noise FBM
    // with 4 octaves is plenty for a Battlezone-style heightmap. Lives
    // in shared so the client + server agree byte-for-byte and we
    // never have to ship the heightmap over the wire (well, we do for
    // first-load, but only as a sanity-check fallback).
    const h2 = (x, y, salt) => {
        const n = (Math.sin(x * 127.1 + y * 311.7 + salt * 74.7) * 43758.5453123);
        return n - Math.floor(n);
    };
    const lerp = (a, b, t) => a + (b - a) * t;
    const smooth = (t) => t * t * (3 - 2 * t);
    const noise = (px, py) => {
        const x0 = Math.floor(px);
        const y0 = Math.floor(py);
        const tx = smooth(px - x0);
        const ty = smooth(py - y0);
        const a = h2(x0, y0, seed);
        const b = h2(x0 + 1, y0, seed);
        const c = h2(x0, y0 + 1, seed);
        const d = h2(x0 + 1, y0 + 1, seed);
        return lerp(lerp(a, b, tx), lerp(c, d, tx), ty);
    };
    // Per-biome shaping. Lava: big crags with sudden drops. Ice/Snow: gently
    // rolling. Rock: jagged. Grass/Dirt: smooth wide hills.
    const params = (() => {
        switch (biome) {
            case BiomeId.Lava: return { freq: 5.5, amp: 1.2, ridge: 0.7 };
            case BiomeId.Rock: return { freq: 6.5, amp: 1.1, ridge: 0.5 };
            case BiomeId.Ice: return { freq: 3.5, amp: 0.7, ridge: 0.0 };
            case BiomeId.Snow: return { freq: 3.2, amp: 0.7, ridge: 0.0 };
            case BiomeId.Grass: return { freq: 2.8, amp: 0.8, ridge: 0.0 };
            case BiomeId.Dirt:
            default: return { freq: 3.0, amp: 0.85, ridge: 0.1 };
        }
    })();
    let lo = Infinity;
    let hi = -Infinity;
    for (let z = 0; z < res; z++) {
        for (let x = 0; x < res; x++) {
            const nx = x / res;
            const nz = z / res;
            let amp = 0.5;
            let freq = params.freq;
            let val = 0;
            let norm = 0;
            for (let o = 0; o < 4; o++) {
                let n = noise(nx * freq, nz * freq);
                if (params.ridge > 0) {
                    // Ridge-noise blend: 1-|2n-1| produces sharp ridge crests.
                    const r = 1 - Math.abs(2 * n - 1);
                    n = n * (1 - params.ridge) + r * params.ridge;
                }
                val += amp * n;
                norm += amp;
                amp *= 0.5;
                freq *= 2.0;
            }
            val = (val / norm) * params.amp;
            // Boundary mountain wall — Battlezone arenas are walled in by
            // a ring of mountains so the player can't sail off the
            // heightmap into void. The ramp is *circular* (radial =
            // sqrt(cx²+cz²)) so the rim reads as a natural crater bowl
            // rather than a fence. Inner ~78 % of the disc is fully
            // playable; the outer band ramps STEEPLY (only ~14 % of the
            // radius wide) to ~2.0× the normal noise amplitude, producing
            // a tall continuous mountain horizon that feeds straight into
            // the ground-clamp gravity loop — ships, NPCs and projectiles
            // all collide with it for free. The narrow band + tall peaks
            // produce a clear "wall" silhouette instead of the previous
            // gentle ramp that read as rolling hills.
            const cx = (x / (res - 1)) * 2 - 1;
            const cz = (z / (res - 1)) * 2 - 1;
            const radial = Math.min(1, Math.sqrt(cx * cx + cz * cz));
            const t = Math.max(0, Math.min(1, (radial - 0.78) / (0.92 - 0.78)));
            // smoothstep^2 — steeper than the previous smoothstep so the
            // wall climbs faster once you cross the boundary.
            const sm = t * t * (3 - 2 * t);
            const rim = sm * sm;
            val = val * (1 - rim) + (params.amp * 2.0) * rim;
            // Cliff terracing — quantises midrange heights into stepped
            // bands so a fraction of slopes read as escarpments rather
            // than smooth hills. Only applies to the 0.30–0.75 band so
            // ridges and valleys keep their natural shape, and only when
            // the biome is a "rocky" one. Strength scales with biome's
            // ridge weight so Lava / Rock get pronounced cliffs and the
            // gentle Snow / Grass biomes stay smooth.
            if (params.ridge > 0.2 && val > 0.30 && val < 0.75) {
                const bandSpan = 0.45;
                const bandT = (val - 0.30) / bandSpan;
                const stepCount = 5;
                const stepped = Math.floor(bandT * stepCount) / stepCount;
                const cliffMix = params.ridge * 0.55;
                val = val * (1 - cliffMix) + (0.30 + stepped * bandSpan) * cliffMix;
            }
            out[z * res + x] = val;
            if (val < lo)
                lo = val;
            if (val > hi)
                hi = val;
        }
    }
    // Normalize to 0..255.
    const span = hi - lo || 1;
    for (let i = 0; i < out.length; i++) {
        const v = out[i];
        const ratio = (v - lo) / span;
        out[i] = Math.max(0, Math.min(255, Math.round(ratio * 255)));
    }
    return out;
}
/**
 * Convert a world-space position (centred on the heightmap, range
 * `[-size/2, size/2]`) into the floor cell index in the row-major
 * 0..255 raw heightmap grid. Returns -1 outside the grid.
 *
 * Used by the post-pass mutators ({@link flattenAroundBases},
 * {@link carvePaths}) to write heights back into the raw `Uint8`
 * encoding without going through the smoothed `sampleHeightmap`
 * (which interpolates and would smear the post-pass writes back
 * into the noise field).
 */
function worldToCellIndex(hmStub, x, z) {
    const cell = hmStub.size / hmStub.heightmapRes;
    const ix = Math.round((x + hmStub.size / 2) / cell);
    const iz = Math.round((z + hmStub.size / 2) / cell);
    if (ix < 0 || iz < 0 || ix >= hmStub.heightmapRes || iz >= hmStub.heightmapRes)
        return null;
    return { ix, iz };
}
/**
 * Flatten a circular disc of the heightmap to the average of its
 * existing values (so each base sits on a believable plateau that
 * reads as "this used to be a hill, then someone bulldozed it for
 * the foundation"). Uses smoothstep falloff between
 * `radius * 0.7` (fully flat) and `radius * 1.4` (untouched) so
 * the plateau blends back into surrounding terrain instead of
 * leaving a hard cliff at the edge.
 */
function flattenAroundBases(heightmap, hmStub, bases) {
    const cell = hmStub.size / hmStub.heightmapRes;
    for (const base of bases) {
        // 1. Sample the average raw heightmap value across the inner
        //    flat zone — this is the plateau elevation we'll snap to.
        const innerR = base.radius * 0.7;
        const outerR = base.radius * 1.4;
        const cellsR = Math.ceil(outerR / cell) + 1;
        const center = worldToCellIndex(hmStub, base.position.x, base.position.z);
        if (!center)
            continue;
        let sum = 0;
        let count = 0;
        for (let dz = -cellsR; dz <= cellsR; dz++) {
            for (let dx = -cellsR; dx <= cellsR; dx++) {
                const ix = center.ix + dx;
                const iz = center.iz + dz;
                if (ix < 0 || iz < 0 || ix >= hmStub.heightmapRes || iz >= hmStub.heightmapRes)
                    continue;
                const wx = (ix * cell) - hmStub.size / 2;
                const wz = (iz * cell) - hmStub.size / 2;
                const dxw = wx - base.position.x;
                const dzw = wz - base.position.z;
                if (dxw * dxw + dzw * dzw > innerR * innerR)
                    continue;
                sum += heightmap[iz * hmStub.heightmapRes + ix];
                count++;
            }
        }
        if (count === 0)
            continue;
        const target = sum / count;
        // 2. Blend toward the target across the [innerR, outerR] band
        //    using smoothstep, so the plateau has a soft skirt.
        for (let dz = -cellsR; dz <= cellsR; dz++) {
            for (let dx = -cellsR; dx <= cellsR; dx++) {
                const ix = center.ix + dx;
                const iz = center.iz + dz;
                if (ix < 0 || iz < 0 || ix >= hmStub.heightmapRes || iz >= hmStub.heightmapRes)
                    continue;
                const wx = (ix * cell) - hmStub.size / 2;
                const wz = (iz * cell) - hmStub.size / 2;
                const dxw = wx - base.position.x;
                const dzw = wz - base.position.z;
                const d = Math.sqrt(dxw * dxw + dzw * dzw);
                if (d > outerR)
                    continue;
                const u = d <= innerR ? 1 : 1 - (d - innerR) / (outerR - innerR);
                const w = u * u * (3 - 2 * u); // smoothstep
                const idx = iz * hmStub.heightmapRes + ix;
                heightmap[idx] = Math.round(heightmap[idx] * (1 - w) + target * w);
            }
        }
    }
}
/**
 * Carve a gentle path between every Spaceport / Outpost / Fortress
 * pair so patrols (and the player) can drive ground vehicles through
 * the rough terrain without constantly clipping cliffs. Implementation
 * is a simple "drag a soft brush along a polyline" — for each pair
 * we rasterise the segment in heightmap-cell steps and pull the
 * nearby cells' raw heights toward a corridor target slightly below
 * the local average. Adds visible roads through the heightmap.
 */
function carvePaths(heightmap, hmStub, bases) {
    if (bases.length < 2)
        return;
    const cell = hmStub.size / hmStub.heightmapRes;
    const half = hmStub.size / 2;
    const root = bases.find((b) => b.kind === BaseKind.Spaceport) ?? bases[0];
    const pathBrushR = 18; // world units of carve radius perpendicular to the path
    const cellsR = Math.ceil(pathBrushR / cell) + 1;
    const carveSegment = (from, to) => {
        const dx = to.x - from.x;
        const dz = to.z - from.z;
        const len = Math.sqrt(dx * dx + dz * dz);
        if (len < 1)
            return;
        const steps = Math.ceil(len / (cell * 0.6));
        for (let s = 0; s <= steps; s++) {
            const t = s / steps;
            const wx = from.x + dx * t;
            const wz = from.z + dz * t;
            // Skip the endpoints (the base flatten already owns them).
            if (s < 4 || s > steps - 4)
                continue;
            const center = worldToCellIndex(hmStub, wx, wz);
            if (!center)
                continue;
            // Don't carve into the rim mountains — clamp the work to the
            // inner playable disc so we never tunnel through the wall.
            const radial = Math.sqrt(wx * wx + wz * wz) / half;
            if (radial > 0.65)
                continue;
            // Sample the local terrain to pick a corridor target slightly
            // below the surrounding average — a soft groove, not a trench.
            let sum = 0;
            let count = 0;
            for (let dzc = -cellsR; dzc <= cellsR; dzc++) {
                for (let dxc = -cellsR; dxc <= cellsR; dxc++) {
                    const ix = center.ix + dxc;
                    const iz = center.iz + dzc;
                    if (ix < 0 || iz < 0 || ix >= hmStub.heightmapRes || iz >= hmStub.heightmapRes)
                        continue;
                    sum += heightmap[iz * hmStub.heightmapRes + ix];
                    count++;
                }
            }
            if (count === 0)
                continue;
            const target = (sum / count) - 8; // ~8/255 = ~3 % below local avg
            for (let dzc = -cellsR; dzc <= cellsR; dzc++) {
                for (let dxc = -cellsR; dxc <= cellsR; dxc++) {
                    const ix = center.ix + dxc;
                    const iz = center.iz + dzc;
                    if (ix < 0 || iz < 0 || ix >= hmStub.heightmapRes || iz >= hmStub.heightmapRes)
                        continue;
                    const dwx = (ix * cell) - half - wx;
                    const dwz = (iz * cell) - half - wz;
                    const d = Math.sqrt(dwx * dwx + dwz * dwz);
                    if (d > pathBrushR)
                        continue;
                    const u = 1 - d / pathBrushR;
                    const w = u * u * (3 - 2 * u) * 0.7; // 70 % max influence
                    const idx = iz * hmStub.heightmapRes + ix;
                    heightmap[idx] = Math.max(0, Math.min(255, Math.round(heightmap[idx] * (1 - w) + target * w)));
                }
            }
        }
    };
    // Spaceport ↔ every other base.
    for (const b of bases) {
        if (b === root)
            continue;
        carveSegment(root.position, b.position);
    }
}
function clampToHeight(bp, pos, hover) {
    return { x: pos.x, y: sampleHeightmap(bp, pos.x, pos.z) + hover, z: pos.z };
}
function generateBases(rng, raceId, hmStub, sectorDist) {
    const half = hmStub.size / 2;
    // Always one Spaceport (player landing anchor) plus 2-4 outposts /
    // fortresses depending on sector depth, plus optionally a Refinery.
    const bases = [];
    const minGap = 140;
    const tryPlace = (kind, radius) => {
        for (let attempt = 0; attempt < 16; attempt++) {
            // Keep bases inside the inner playable area (rim ramp starts at
            // 0.70 normalised in `fbmHeightmap`, see boundary mountain
            // ridge comment) so spaceports and outposts aren't built into
            // the side of the perimeter mountains.
            const x = rng.nextFloat(-half * 0.62, half * 0.62);
            const z = rng.nextFloat(-half * 0.62, half * 0.62);
            let collide = false;
            for (const b of bases) {
                const dx = b.position.x - x;
                const dz = b.position.z - z;
                if (dx * dx + dz * dz < (b.radius + radius + minGap) ** 2) {
                    collide = true;
                    break;
                }
            }
            if (collide)
                continue;
            const pos = clampToHeight(hmStub, { x, y: 0, z }, 0);
            return { position: pos, radius, kind, raceId };
        }
        return null;
    };
    // Spaceport always first so the player spawn is well-defined.
    const port = tryPlace(BaseKind.Spaceport, 35);
    if (port)
        bases.push(port);
    const extraCount = sectorDist < 20 ? 2 :
        sectorDist < 60 ? 3 :
            rng.nextInt(3, 4);
    for (let i = 0; i < extraCount; i++) {
        const kind = rng.next() < 0.35 ? BaseKind.Fortress : BaseKind.Outpost;
        const radius = kind === BaseKind.Fortress ? 32 : 22;
        const b = tryPlace(kind, radius);
        if (b)
            bases.push(b);
    }
    if (sectorDist > 30 && rng.next() < 0.6) {
        const refinery = tryPlace(BaseKind.Refinery, 28);
        if (refinery)
            bases.push(refinery);
    }
    return bases;
}
function pickPatchResource(rng, biome, raceId, sectorDist) {
    // Combine the biome-specific bias + the race-specific resource pool +
    // a sector-distance gate. Anything that fails the distance gate is
    // dropped; if nothing remains we fall back to ScrapMetal (always
    // legal anywhere).
    const candidates = [];
    const consider = (t, weight) => {
        const info = RESOURCE_INFO[t];
        if (!info || sectorDist < info.minSectorDistance)
            return;
        for (let w = 0; w < weight; w++)
            candidates.push(t);
    };
    for (const t of BIOME_RESOURCE_BIAS[biome])
        consider(t, 4);
    const racePool = RACE_RESOURCES[raceId] ?? [];
    for (const t of racePool)
        consider(t, 2);
    // Always have at least the basics in the pool so we never crash on a
    // new biome combination.
    for (const t of [RT.ScrapMetal, RT.IronOre, RT.Silicate])
        consider(t, 1);
    if (candidates.length === 0)
        return RT.ScrapMetal;
    return rng.pick(candidates);
}
function generateResourcePatches(rng, biome, raceId, hmStub, bases, sectorDist) {
    const half = hmStub.size / 2;
    const patches = [];
    // Scale the patch count with the playable area (1.0× at the
    // legacy 1600 u size, 1.75× at the v6 2800 u size). We round so a
    // small arena still gets at least the minimum count.
    const areaScale = hmStub.size / 1600;
    const baseCount = Math.max(5, Math.round((5 + Math.min(4, Math.floor(sectorDist / 25))) * areaScale));
    const refineryBoost = bases.filter((b) => b.kind === BaseKind.Refinery).length * 2;
    const total = baseCount + refineryBoost;
    for (let i = 0; i < total; i++) {
        let cx = rng.nextFloat(-half * 0.62, half * 0.62);
        let cz = rng.nextFloat(-half * 0.62, half * 0.62);
        // Bias half the patches near refineries so the building actually
        // *means* something.
        const refineries = bases.filter((b) => b.kind === BaseKind.Refinery);
        if (refineries.length > 0 && rng.next() < 0.5) {
            const ref = rng.pick(refineries);
            const a = rng.nextFloat(0, Math.PI * 2);
            const r = rng.nextFloat(60, 160);
            cx = ref.position.x + Math.cos(a) * r;
            cz = ref.position.z + Math.sin(a) * r;
            cx = Math.max(-half * 0.65, Math.min(half * 0.65, cx));
            cz = Math.max(-half * 0.65, Math.min(half * 0.65, cz));
        }
        const center = clampToHeight(hmStub, { x: cx, y: 0, z: cz }, 0);
        patches.push({
            center,
            count: rng.nextInt(4, 9),
            radius: rng.nextFloat(15, 35),
            resourceType: pickPatchResource(rng, biome, raceId, sectorDist),
        });
    }
    return patches;
}
/**
 * Produce a polyline patrol route between two bases.
 *
 * This is the "A*" the design called for, intentionally cheap rather
 * than a full grid search: we sample the heightmap along a few jittered
 * waypoints and keep the polyline short. Real grid A* over 128² would
 * be 2-3 ms per pair which adds up at gen time and we'd have to carry
 * a much bigger polyline through the wire/Mongo. NPCs follow the
 * polyline verbatim so the difference is invisible to gameplay.
 */
function generatePatrolPath(rng, hmStub, from, to) {
    const segments = 6;
    const waypoints = [];
    for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const x = from.x + (to.x - from.x) * t + rng.nextFloat(-30, 30);
        const z = from.z + (to.z - from.z) * t + rng.nextFloat(-30, 30);
        const cx = Math.max(-hmStub.size / 2, Math.min(hmStub.size / 2, x));
        const cz = Math.max(-hmStub.size / 2, Math.min(hmStub.size / 2, z));
        waypoints.push({
            x: cx,
            y: sampleHeightmap(hmStub, cx, cz) + SURFACE_HOVER_OFFSET * 2,
            z: cz,
        });
    }
    return { waypoints };
}
function generatePatrolNetwork(rng, hmStub, bases) {
    const paths = [];
    if (bases.length < 2)
        return paths;
    // Connect the spaceport (or the first base) to every other base, then
    // chain the rest in a ring so patrols form a connected network.
    const root = bases.find((b) => b.kind === BaseKind.Spaceport) ?? bases[0];
    for (const b of bases) {
        if (b === root)
            continue;
        paths.push(generatePatrolPath(rng, hmStub, root.position, b.position));
    }
    for (let i = 1; i < bases.length - 1; i++) {
        paths.push(generatePatrolPath(rng, hmStub, bases[i].position, bases[i + 1].position));
    }
    return paths;
}
const SURFACE_NPC_CLASSES_BY_TIER = [
    // Surface NPCs use the same hull strings as space NPCs but bias
    // toward agile classes so they read as "low-flying gunships /
    // interceptors" rather than capital-class ground assault. Capital
    // ships still spawn from space sectors, not from the surface.
    ['scout', 'fighter', 'interceptor'],
    ['scout', 'fighter', 'interceptor', 'gunboat'],
    ['fighter', 'interceptor', 'gunboat', 'corvette'],
    ['interceptor', 'gunboat', 'corvette', 'gunship'],
    ['interceptor', 'corvette', 'gunship', 'frigate'],
];
function pickSurfaceShipClass(rng, sectorDist) {
    const tier = Math.min(SURFACE_NPC_CLASSES_BY_TIER.length - 1, Math.floor(sectorDist / 30));
    const pool = SURFACE_NPC_CLASSES_BY_TIER[tier];
    return pool[rng.nextInt(0, pool.length - 1)];
}
function generateSurfaceNPCs(rng, bases, hmStub, raceId, sectorDist, pirateOverride) {
    const out = [];
    const raceInfo = RACE_INFO[raceId];
    // 0 = hostile / 2 = friendly. Surface NPCs use the same disposition
    // mapping convention as space NPCs (see DISPOSITION_MAP in sector.ts).
    // `pirateOverride` forces the garrison hostile regardless of the
    // nominal owner race — used to scatter a small number of
    // "contested world" planets where even a friendly system has
    // pirate squatters fighting for the resource patches.
    const baseDisposition = pirateOverride ? 0 : (() => {
        const d = raceInfo?.disposition;
        if (d === 'hostile' || d === 'unknown')
            return 0;
        if (d === 'friendly')
            return 2;
        return 1;
    })();
    for (let bi = 0; bi < bases.length; bi++) {
        const base = bases[bi];
        let count;
        switch (base.kind) {
            case BaseKind.Spaceport:
                count = 2;
                break; // always passive escort
            case BaseKind.Fortress:
                count = 4 + Math.min(3, Math.floor(sectorDist / 30));
                break;
            case BaseKind.Refinery:
                count = 2;
                break;
            case BaseKind.Outpost:
            default:
                count = 2 + Math.min(2, Math.floor(sectorDist / 40));
                break;
        }
        // Pirate planet: on top of (or in place of) the owning-race
        // garrison the generator may decide this world is contested.
        // The decision is made ONCE outside this loop and recorded as
        // `forcePirate`; here we just flip the per-base disposition.
        // Spaceport always stays neutral so the player can land
        // somewhere safe regardless of how rowdy the rest of the
        // surface is.
        const disposition = base.kind === BaseKind.Spaceport ? 1 : baseDisposition;
        const baseLevel = Math.max(1, 1 + Math.floor(sectorDist / 8));
        for (let n = 0; n < count; n++) {
            const a = rng.nextFloat(0, Math.PI * 2);
            const r = rng.nextFloat(base.radius * 1.4, base.radius * 3.0);
            const x = base.position.x + Math.cos(a) * r;
            const z = base.position.z + Math.sin(a) * r;
            const cx = Math.max(-hmStub.size / 2 + 5, Math.min(hmStub.size / 2 - 5, x));
            const cz = Math.max(-hmStub.size / 2 + 5, Math.min(hmStub.size / 2 - 5, z));
            const y = sampleHeightmap(hmStub, cx, cz) + SURFACE_HOVER_OFFSET * 2;
            out.push({
                position: { x: cx, y, z: cz },
                disposition,
                level: baseLevel + rng.nextInt(0, 2),
                shipClass: pickSurfaceShipClass(rng, sectorDist),
                patrolRadius: rng.nextFloat(60, 140),
                raceId,
                baseIndex: bi,
            });
        }
    }
    return out;
}
function pickSurfaceName(seed, raceId, planetIndex) {
    const p = hash(seed, raceId, planetIndex, 0xa1) % SURFACE_NAME_PREFIXES.length;
    const s = hash(seed, raceId, planetIndex, 0xb2) % SURFACE_NAME_SUFFIXES.length;
    return `${SURFACE_NAME_PREFIXES[p]} ${SURFACE_NAME_SUFFIXES[s]}`;
}
/**
 * Per-biome flora composition. Each entry pairs a `FloraKind` with a
 * weight; the generator picks kinds proportionally and then biases
 * count + scale based on biome density. Lava picks sparse shards;
 * Grass goes lush with trees + mushrooms; Ice gets crystal pillars.
 */
const FLORA_BY_BIOME = {
    // Scale ranges below are tuned alongside the per-kind base mesh
    // size in `client/src/procedural/surface-renderer.ts` and the
    // collider radii in {@link FLORA_COLLIDE_BASE_RADIUS}. Increasing
    // a `scaleRange[1]` linearly grows both the visual mesh AND the
    // collider, so the player never feels they're being pushed by an
    // invisible cylinder larger than what they can see.
    //
    // v6 mixes in the new alien props (AlienBulb / TwistedSpire /
    // JellyFlora) so even an "earth-like" Grass world reads as
    // visibly alien from the cockpit instead of "Earth but with
    // glowing crystals on Snow worlds".
    [BiomeId.Grass]: {
        kinds: [FloraKind.Tree, FloraKind.Shrub, FloraKind.Mushroom, FloraKind.Boulder, FloraKind.AlienBulb],
        weights: [0.40, 0.20, 0.15, 0.10, 0.15],
        density: 1.0,
        scaleRange: [1.4, 2.4],
    },
    [BiomeId.Dirt]: {
        kinds: [FloraKind.Tree, FloraKind.Shrub, FloraKind.Boulder, FloraKind.AlienBulb, FloraKind.TwistedSpire],
        weights: [0.25, 0.25, 0.30, 0.10, 0.10],
        density: 0.7,
        scaleRange: [1.2, 2.0],
    },
    [BiomeId.Snow]: {
        kinds: [FloraKind.Tree, FloraKind.IcePillar, FloraKind.Crystal, FloraKind.Boulder, FloraKind.JellyFlora],
        weights: [0.25, 0.25, 0.20, 0.15, 0.15],
        density: 0.8,
        scaleRange: [1.2, 2.2],
    },
    [BiomeId.Ice]: {
        kinds: [FloraKind.IcePillar, FloraKind.Crystal, FloraKind.Boulder, FloraKind.JellyFlora],
        weights: [0.40, 0.30, 0.10, 0.20],
        density: 0.6,
        scaleRange: [1.4, 2.4],
    },
    [BiomeId.Rock]: {
        kinds: [FloraKind.Boulder, FloraKind.Crystal, FloraKind.Shrub, FloraKind.TwistedSpire],
        weights: [0.50, 0.20, 0.10, 0.20],
        density: 0.7,
        scaleRange: [1.3, 2.2],
    },
    [BiomeId.Lava]: {
        kinds: [FloraKind.LavaShard, FloraKind.Boulder, FloraKind.Crystal, FloraKind.TwistedSpire],
        weights: [0.45, 0.25, 0.10, 0.20],
        density: 0.4,
        scaleRange: [1.0, 1.8],
    },
};
function pickWeightedFloraKind(rng, kinds, weights) {
    const total = weights.reduce((a, b) => a + b, 0);
    let r = rng.next() * total;
    for (let i = 0; i < kinds.length; i++) {
        r -= weights[i];
        if (r <= 0)
            return kinds[i];
    }
    return kinds[0];
}
/**
 * Scatter biome-appropriate decorative props across the playable
 * inner area. Excludes:
 *   - inside any base footprint (`+ buffer`),
 *   - inside any resource patch (so mineable nodes aren't visually
 *     overlapped by trees),
 *   - the rim mountain band (any radial > 0.62).
 *
 * Density is capped: ~80–120 instances at default density. Cheap to
 * generate (~1 ms for 100 instances) and cheap to render (one
 * instanced draw call per kind in the SurfaceRenderer).
 */
function generateFlora(rng, biome, hmStub, bases, resourcePatches, lavaPools) {
    const half = hmStub.size / 2;
    const cfg = FLORA_BY_BIOME[biome];
    // Scale the flora budget linearly with the playable area (1.0× at
    // the legacy 1600 u size, 1.75× at the v6 2800 u size) so a larger
    // arena still reads as a dense biome instead of a sparse field.
    const areaScale = hmStub.size / 1600;
    const baseCount = Math.round(110 * cfg.density * areaScale);
    const out = [];
    // Track the (x, z, r) of every collidable instance we've already
    // placed so we can reject candidate positions whose collider
    // would overlap an existing one. Without this the procedural
    // flora occasionally clusters two collidable trees within a few
    // units of each other, producing a hairy little wall the player
    // can't navigate around. We only track collidable kinds so the
    // small filler shrubs / lava shards remain free to overlap
    // visually for that "thicket" look without creating physics.
    const placedColliders = [];
    // Wide enough that even a Mothership-sized ship (radius ~6) can
    // squeeze through a two-tree gap without snagging on either
    // trunk; tighter than this and the procedural forest occasionally
    // produces "I tried to fly between those trees and the ship just
    // froze" funnels that the user reported as part of the
    // freeze-while-moving symptom.
    const COLLIDER_GAP = 8;
    for (let i = 0; i < baseCount * 4 && out.length < baseCount; i++) {
        const x = rng.nextFloat(-half * 0.62, half * 0.62);
        const z = rng.nextFloat(-half * 0.62, half * 0.62);
        // Reject points inside or near a base.
        let baseHit = false;
        for (const b of bases) {
            const dx = b.position.x - x;
            const dz = b.position.z - z;
            const buf = b.radius * 1.6;
            if (dx * dx + dz * dz < buf * buf) {
                baseHit = true;
                break;
            }
        }
        if (baseHit)
            continue;
        // Reject points inside a resource patch — visually noisy and
        // makes mining harder than it should be.
        let patchHit = false;
        for (const p of resourcePatches) {
            const dx = p.center.x - x;
            const dz = p.center.z - z;
            if (dx * dx + dz * dz < p.radius * p.radius) {
                patchHit = true;
                break;
            }
        }
        if (patchHit)
            continue;
        // Reject points inside a lava pool — visual: trees floating in
        // molten rock; physics: the player would take per-tick damage
        // standing next to / inside one of these and getting blocked by
        // a tree at the same time, which feels unfair.
        let lavaHit = false;
        for (const lp of lavaPools) {
            const dx = lp.position.x - x;
            const dz = lp.position.z - z;
            if (dx * dx + dz * dz < lp.radius * lp.radius) {
                lavaHit = true;
                break;
            }
        }
        if (lavaHit)
            continue;
        const kind = pickWeightedFloraKind(rng, cfg.kinds, cfg.weights);
        const scale = rng.nextFloat(cfg.scaleRange[0], cfg.scaleRange[1]);
        // Compute the new instance's collider radius — we need it both
        // to test against existing placed colliders AND to record for
        // future rejections.
        const candidate = {
            position: { x, y: 0, z },
            kind,
            scale,
            rotY: rng.nextFloat(0, Math.PI * 2),
        };
        const candR = floraCollisionRadius(candidate);
        if (candR > 0) {
            let blocked = false;
            for (const c of placedColliders) {
                const dx = c.x - x;
                const dz = c.z - z;
                const minD = c.r + candR + COLLIDER_GAP;
                if (dx * dx + dz * dz < minD * minD) {
                    blocked = true;
                    break;
                }
            }
            if (blocked)
                continue;
            placedColliders.push({ x, z, r: candR });
        }
        candidate.position.y = sampleHeightmap(hmStub, x, z);
        out.push(candidate);
    }
    return out;
}
/**
 * Build the per-surface lava-pool hazard list.
 *
 * - Lava biome: drop 4–7 large pools across the inner playable
 *   area. These exist on top of the already-molten-looking ground
 *   so the renderer has explicit disc markers to paint glowing
 *   patches, and the server hazard tick can skip the heightmap
 *   sample for the dominant case (full Lava biome ground damage
 *   is handled by the biome check, not these pools).
 * - Dirt / Rock biome: ~40 % chance of a single small pool somewhere
 *   on the map — a cratered lava lake as flavour + hazard. Anywhere
 *   else: no pools.
 *
 * Pools are kept away from bases (so the player's spaceport isn't
 * in a fire pit) and resource patches (so mining doesn't auto-cook
 * the player).
 */
function generateLavaPools(rng, biome, hmStub, bases, resourcePatches) {
    const half = hmStub.size / 2;
    const areaScale = hmStub.size / 1600;
    let count = 0;
    let radiusRange = [30, 60];
    if (biome === BiomeId.Lava) {
        count = Math.round(rng.nextInt(4, 7) * areaScale);
        radiusRange = [60, 110];
    }
    else if ((biome === BiomeId.Dirt || biome === BiomeId.Rock)
        && rng.next() < 0.4) {
        count = rng.nextInt(1, 2);
        radiusRange = [25, 50];
    }
    else {
        return [];
    }
    const out = [];
    const minGap = 40;
    for (let attempt = 0; attempt < count * 12 && out.length < count; attempt++) {
        const x = rng.nextFloat(-half * 0.55, half * 0.55);
        const z = rng.nextFloat(-half * 0.55, half * 0.55);
        const r = rng.nextFloat(radiusRange[0], radiusRange[1]);
        // Stay clear of bases — pools inside an outpost would melt the
        // garrison and turn the spawn into a deathtrap.
        let baseHit = false;
        for (const b of bases) {
            const dx = b.position.x - x;
            const dz = b.position.z - z;
            const safe = b.radius * 1.8 + r;
            if (dx * dx + dz * dz < safe * safe) {
                baseHit = true;
                break;
            }
        }
        if (baseHit)
            continue;
        // Stay clear of resource patches — same idea, mining shouldn't
        // come with a passive damage tick.
        let patchHit = false;
        for (const p of resourcePatches) {
            const dx = p.center.x - x;
            const dz = p.center.z - z;
            const safe = p.radius + r;
            if (dx * dx + dz * dz < safe * safe) {
                patchHit = true;
                break;
            }
        }
        if (patchHit)
            continue;
        // Stay clear of other pools so they read as separate craters
        // rather than one big molten lake.
        let poolHit = false;
        for (const lp of out) {
            const dx = lp.position.x - x;
            const dz = lp.position.z - z;
            const safe = lp.radius + r + minGap;
            if (dx * dx + dz * dz < safe * safe) {
                poolHit = true;
                break;
            }
        }
        if (poolHit)
            continue;
        out.push({
            position: { x, y: sampleHeightmap(hmStub, x, z), z },
            radius: r,
        });
    }
    return out;
}
/**
 * Generate the per-surface ruin / dig site list. Always 1-4 sites;
 * placement avoids bases and lava pools so the Investigate / Excavate
 * interaction is never accidentally inside a hostile garrison or
 * standing in a lava pool.
 *
 * The site `id` encodes the surface id + ruin index so callers in the
 * mission generator and the codex unlock pipeline can refer to a
 * specific ruin without leaking blueprint internals.
 */
function generateRuins(rng, surfaceKey, hmStub, bases, lavaPools) {
    const half = hmStub.size / 2;
    const areaScale = hmStub.size / 1600;
    const count = Math.max(1, Math.round(rng.nextInt(2, 4) * areaScale));
    const out = [];
    for (let attempt = 0; attempt < count * 16 && out.length < count; attempt++) {
        const x = rng.nextFloat(-half * 0.6, half * 0.6);
        const z = rng.nextFloat(-half * 0.6, half * 0.6);
        const radius = rng.nextFloat(18, 28);
        // Stay clear of bases — a Precursor obelisk inside an outpost
        // would clip the buildings and confuse the safe-zone radius.
        let baseHit = false;
        for (const b of bases) {
            const dx = b.position.x - x;
            const dz = b.position.z - z;
            const safe = b.radius * 1.6 + radius + 40;
            if (dx * dx + dz * dz < safe * safe) {
                baseHit = true;
                break;
            }
        }
        if (baseHit)
            continue;
        // Stay clear of lava pools — investigating inside a pool would
        // damage the player while they hold KeyE.
        let poolHit = false;
        for (const lp of lavaPools) {
            const dx = lp.position.x - x;
            const dz = lp.position.z - z;
            const safe = lp.radius + radius + 20;
            if (dx * dx + dz * dz < safe * safe) {
                poolHit = true;
                break;
            }
        }
        if (poolHit)
            continue;
        // Spread ruins out so they read as separate sites.
        let ruinHit = false;
        for (const existing of out) {
            const dx = existing.position.x - x;
            const dz = existing.position.z - z;
            const safe = existing.radius + radius + 120;
            if (dx * dx + dz * dz < safe * safe) {
                ruinHit = true;
                break;
            }
        }
        if (ruinHit)
            continue;
        const kindRoll = rng.next();
        const kind = kindRoll < 0.4 ? RuinKind.Crash
            : kindRoll < 0.75 ? RuinKind.Colony
                : RuinKind.Precursor;
        const idx = out.length;
        out.push({
            id: `ruin_${surfaceKey}_${idx}`,
            position: { x, y: sampleHeightmap(hmStub, x, z), z },
            radius,
            kind,
            // Deterministic per-ruin lore fragment id. The actual entry
            // text is registered in the shared codex registry; only the
            // pointer travels with the blueprint.
            loreFragmentId: `lore_${kind === RuinKind.Crash ? 'crash' : kind === RuinKind.Precursor ? 'precursor' : 'colony'}_${idx}`,
        });
    }
    return out;
}
/**
 * Per-biome fauna roster. Each entry pairs a `FaunaKind` with a
 * relative weight; the generator picks weighted-random per spawn.
 * Empty roster (no biome match) ⇒ no fauna for that biome.
 */
const FAUNA_BY_BIOME = {
    [BiomeId.Rock]: { kinds: [FaunaKind.RockGrub], weights: [1.0], count: [4, 8] },
    [BiomeId.Lava]: { kinds: [FaunaKind.LavaWyrm], weights: [1.0], count: [3, 6] },
    [BiomeId.Ice]: { kinds: [FaunaKind.IceSerpent], weights: [1.0], count: [3, 6] },
    [BiomeId.Snow]: { kinds: [FaunaKind.IceSerpent], weights: [1.0], count: [2, 5] },
    [BiomeId.Grass]: { kinds: [FaunaKind.BiofluxStrider], weights: [1.0], count: [4, 8] },
    [BiomeId.Dirt]: { kinds: [FaunaKind.DustReaver], weights: [1.0], count: [3, 6] },
};
function generateFauna(rng, hmStub, bases, lavaPools, biome, sectorDist) {
    const roster = FAUNA_BY_BIOME[biome];
    if (!roster || roster.kinds.length === 0)
        return [];
    const half = hmStub.size / 2;
    const areaScale = hmStub.size / 1600;
    const [minC, maxC] = roster.count;
    const target = Math.max(0, Math.round(rng.nextInt(minC, maxC) * areaScale));
    const out = [];
    const baseLevel = Math.max(1, 1 + Math.floor(sectorDist / 10));
    const wsum = roster.weights.reduce((a, b) => a + b, 0);
    for (let attempt = 0; attempt < target * 8 && out.length < target; attempt++) {
        const x = rng.nextFloat(-half * 0.85, half * 0.85);
        const z = rng.nextFloat(-half * 0.85, half * 0.85);
        // Don't spawn fauna right on top of a base — the friendly garrison
        // would shred them in two seconds. Keep a safety gap.
        let baseHit = false;
        for (const b of bases) {
            const dx = b.position.x - x;
            const dz = b.position.z - z;
            const safe = b.radius * 2.2;
            if (dx * dx + dz * dz < safe * safe) {
                baseHit = true;
                break;
            }
        }
        if (baseHit)
            continue;
        // Keep fauna out of lava pools (they'd die instantly).
        let poolHit = false;
        for (const lp of lavaPools) {
            const dx = lp.position.x - x;
            const dz = lp.position.z - z;
            const safe = lp.radius + 8;
            if (dx * dx + dz * dz < safe * safe) {
                poolHit = true;
                break;
            }
        }
        if (poolHit)
            continue;
        const roll = rng.nextFloat(0, wsum);
        let acc = 0;
        let chosen = roster.kinds[0];
        for (let i = 0; i < roster.kinds.length; i++) {
            acc += roster.weights[i];
            if (roll < acc) {
                chosen = roster.kinds[i];
                break;
            }
        }
        out.push({
            position: { x, y: sampleHeightmap(hmStub, x, z) + 2, z },
            kind: chosen,
            level: baseLevel + rng.nextInt(0, 2),
        });
    }
    return out;
}
/**
 * Deterministic surface generator.
 *
 * Given the parent sector coord + planet index, always produces the
 * same `SurfaceBlueprint`. Pure function — no IO, no randomness beyond
 * the seeded `SeededRandom` derived from `MASTER_SEED`. Cheap enough
 * (~0.5-2 ms on a 128² heightmap) to call from a unit test or a UI
 * preview without persistence.
 */
export function generateSurface(parentSector, planetIndex, raceId) {
    const seed = hash(MASTER_SEED ^ 0x5AFE_DA1A, parentSector.x, parentSector.y, parentSector.z, planetIndex);
    const rng = new SeededRandom(seed);
    const sectorDist = Math.sqrt(parentSector.x * parentSector.x +
        parentSector.y * parentSector.y +
        parentSector.z * parentSector.z);
    const biome = pickBiome(rng, raceId, sectorDist);
    const heightmap = fbmHeightmap(seed, SURFACE_HEIGHTMAP_RES, biome);
    const hmStub = {
        heightmap,
        heightmapRes: SURFACE_HEIGHTMAP_RES,
        maxHeight: SURFACE_MAX_HEIGHT,
        size: SURFACE_SIZE,
    };
    const bases = generateBases(rng, raceId, hmStub, sectorDist);
    // Two terrain post-passes: first carve gentle valleys between
    // bases (so patrols / vehicles have natural roads), then flatten
    // the ground around each base into a believable plateau. Order
    // matters — flatten last so the carved corridor doesn't dip into
    // the plateau and break it back into a slope. After both passes
    // we re-sample every base's `position.y` so the spawn points
    // actually sit on the (now-different) terrain.
    carvePaths(heightmap, hmStub, bases);
    flattenAroundBases(heightmap, hmStub, bases);
    for (const b of bases) {
        b.position.y = sampleHeightmap(hmStub, b.position.x, b.position.z);
    }
    const resourcePatches = generateResourcePatches(rng, biome, raceId, hmStub, bases, sectorDist);
    const patrolPaths = generatePatrolNetwork(rng, hmStub, bases);
    // Pirate override: ~18 % of worlds get a hostile garrison
    // regardless of the nominal owner race (lore: contested frontier,
    // raider holdouts, etc.). Capped so the player isn't dropping
    // into a war zone on every other planet. Computed BEFORE the
    // NPC pass so the disposition decision is part of the seeded
    // generation (deterministic across clients + the server).
    const pirateOverride = rng.next() < 0.18;
    const npcs = generateSurfaceNPCs(rng, bases, hmStub, raceId, sectorDist, pirateOverride);
    const lavaPools = generateLavaPools(rng, biome, hmStub, bases, resourcePatches);
    const ruins = generateRuins(rng, surfaceId(parentSector, planetIndex), hmStub, bases, lavaPools);
    const fauna = generateFauna(rng, hmStub, bases, lavaPools, biome, sectorDist);
    const flora = generateFlora(rng, biome, hmStub, bases, resourcePatches, lavaPools);
    // Player lands at the spaceport (or the first base, or a safe
    // fallback near the centre if base generation collapsed).
    // The spawn Y must clear the *entire* Spaceport silhouette
    // (tower ~17 u, lamps ~10 u above the slab) AND the base group's
    // ~2 u tall slab — otherwise large hulls (Mothership visual scale
    // 48) materialise *inside* the pad and the renderer paints the
    // ship below the terrain mesh. We ALSO sample the heightmap
    // directly under the spawn point and pick the higher of the two
    // (port plateau + clearance vs. raw heightmap + safety) so a
    // spaceport that ended up next to a tall mountain rim still
    // spawns the player above ground.
    const SPAWN_CLEARANCE = 60;
    const port = bases.find((b) => b.kind === BaseKind.Spaceport) ?? bases[0];
    const spawnX = port ? port.position.x : 0;
    const spawnZ = port ? port.position.z + 12 : 0;
    const spawnGround = sampleHeightmap(hmStub, spawnX, spawnZ);
    const baseY = port ? port.position.y : spawnGround;
    const spawnPos = {
        x: spawnX,
        y: Math.max(baseY, spawnGround) + SPAWN_CLEARANCE,
        z: spawnZ,
    };
    return {
        id: surfaceId(parentSector, planetIndex),
        parentSector: { x: parentSector.x, y: parentSector.y, z: parentSector.z },
        planetIndex,
        raceId,
        biome,
        size: SURFACE_SIZE,
        heightmapRes: SURFACE_HEIGHTMAP_RES,
        maxHeight: SURFACE_MAX_HEIGHT,
        heightmap,
        blueprintVersion: SURFACE_BLUEPRINT_VERSION,
        seed,
        name: pickSurfaceName(seed, raceId, planetIndex),
        spawn: { position: spawnPos },
        bases,
        resourcePatches,
        patrolPaths,
        npcs,
        flora,
        lavaPools,
        ruins,
        fauna,
        pirateOverride,
    };
}
/** Resource sample of a tier — kept here as a leak-free reference. */
export const _ALL_RESOURCE_TIERS_REF = ALL_RESOURCE_TIERS;
// `SECTOR_SIZE` is intentionally re-exported for tests + tooling that
// want to compose surface coords against the parent grid.
export const SURFACE_PARENT_SECTOR_SIZE = SECTOR_SIZE;
//# sourceMappingURL=surface.js.map